import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { SectionId } from '../types';

export const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: SectionId) => {
    setMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Text color logic: Light when at top (over dark video), Dark when scrolled (over light bg)
  const textColorClass = scrolled ? 'text-stone-900' : 'text-stone-100';
  const subTextColorClass = scrolled ? 'text-stone-600' : 'text-stone-300';
  const hoverColorClass = scrolled ? 'hover:text-stone-900' : 'hover:text-white';
  const navLineClass = scrolled ? 'after:bg-stone-800' : 'after:bg-stone-100';

  return (
    <>
      <header 
        className={`fixed top-0 w-full z-50 transition-all duration-500 ${
          scrolled ? 'bg-stone-50/90 backdrop-blur-md py-4 shadow-sm' : 'bg-transparent py-8'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex justify-between items-center">
          <div 
            onClick={() => scrollTo(SectionId.HERO)}
            className="cursor-pointer z-50 relative group"
          >
            <h1 className={`font-logo text-3xl md:text-4xl transition-colors duration-500 ${textColorClass} group-hover:opacity-80`}>
              Anavaran.
            </h1>
            <p className={`text-[9px] tracking-[0.3em] text-center uppercase -mt-1 ml-1 transition-colors duration-500 ${subTextColorClass}`}>
              Films
            </p>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex gap-12 items-center">
            {[
              { label: 'Work', id: SectionId.WORK },
              { label: 'Gallery', id: SectionId.GALLERY },
              { label: 'Philosophy', id: SectionId.PHILOSOPHY },
              { label: 'Contact', id: SectionId.CONTACT },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`text-xs uppercase tracking-[0.2em] font-sans transition-colors duration-500 relative after:content-[''] after:absolute after:-bottom-2 after:left-0 after:w-0 after:h-[1px] after:transition-all hover:after:w-full ${textColorClass} ${hoverColorClass} ${navLineClass}`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button 
            className={`md:hidden z-50 transition-colors duration-500 ${menuOpen ? 'text-stone-800' : textColorClass}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={24} strokeWidth={1} /> : <Menu size={24} strokeWidth={1} />}
          </button>
        </div>
      </header>

      {/* Mobile Nav Overlay */}
      <div className={`fixed inset-0 bg-stone-50 z-40 transition-transform duration-700 cubic-bezier(0.16, 1, 0.3, 1) ${menuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="h-full flex flex-col justify-center items-center gap-8">
          {[
              { label: 'Work', id: SectionId.WORK },
              { label: 'Gallery', id: SectionId.GALLERY },
              { label: 'Philosophy', id: SectionId.PHILOSOPHY },
              { label: 'Contact', id: SectionId.CONTACT },
          ].map((item, idx) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              style={{ transitionDelay: `${100 * idx}ms` }}
              className={`text-3xl font-serif text-stone-800 transition-all duration-500 ${menuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};